import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import DashboardContent from "./DashboardContent";
import AllServices from "./AllServices";
import AddNewService from "./AddNewService";
import OfferImage from "./OffersAndPromotionForMSILADMIN/OffersAndPromotions";
import MSILUsageReport from "./MSILUsageReport";
import NotificationSettings from "./NotificationSettings";

const ParentDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Fixed route mapping
  const tabRouteMap = {
    dashboard: "/AdminHome/dashboard",
    services: "/AdminHome/services",
    reports: "/AdminHome/reports",
    notifications: "/AdminHome/notifications",
    offersAndPromotions: "/AdminHome/offers-and-promotions",
  };

  const routeTabMap = Object.fromEntries(
    Object.entries(tabRouteMap).map(([key, value]) => [value, key])
  );

  const [activeTab, setActiveTab] = useState(() => {
    return routeTabMap[location.pathname] || "dashboard";
  });
  

  useEffect(() => {
    // CRITICAL FIX: Only navigate if we're not in the middle of OAuth callback
    // Check if URL has code parameter (OAuth callback) - don't navigate during callback
    const hasOAuthCode = new URLSearchParams(location.search).has('code');
    if (hasOAuthCode) {
      // OAuth callback in progress - don't navigate yet
      return;
    }
    
    const newPath = tabRouteMap[activeTab];
    if (newPath && location.pathname !== newPath) {
      navigate(newPath);
    }
  }, [activeTab, navigate, location.pathname]);

  const [isAddingNewService, setIsAddingNewService] = useState(false);
  const [isAddingOfferImage, setIsAddingOfferImage] = useState(false);
  const [isAddingReports, setIsAddingReports] = useState(false);
  const [isAddingNotifications, setIsAddingNotifications] = useState(false);

  // KEEP-ALIVE TABS: once a tab has been visited, keep its component mounted
  // (just hidden via CSS) instead of unmounting it, so its useEffect-driven
  // API calls don't re-fire on every tab switch. Resets on a full page refresh.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));

  useEffect(() => {
    setVisitedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  const tabStyle = (tabKey) => ({
    display: activeTab === tabKey ? "block" : "none",
  });

  return (
    <div className="d-flex">
      <Sidebar
        options={Object.keys(tabRouteMap).map((key) => ({
          key,
          name: t(key), // translated label
          icon: sidebarIcon(key),
        }))}
        activeTab={activeTab}
        onSelect={(selectedKey) => setActiveTab(selectedKey)} // get key, not label
      />
      <div className="col-md-11 bg-light py-4 px-3">
        {visitedTabs.has("dashboard") && (
          <div style={tabStyle("dashboard")}>
            <DashboardContent />
          </div>
        )}

        {visitedTabs.has("services") && (
          <div style={tabStyle("services")}>
            {isAddingNewService ? (
              <AddNewService onClose={() => setIsAddingNewService(false)} />
            ) : (
              <AllServices onAddServiceClick={() => setIsAddingNewService(true)} />
            )}
          </div>
        )}

        {visitedTabs.has("reports") && (
          <div style={tabStyle("reports")}>
            {isAddingReports ? (
              <MSILUsageReport onClose={() => setIsAddingReports(false)} />
            ) : (
              <MSILUsageReport onAddServiceClick={() => setIsAddingReports(true)} />
            )}
          </div>
        )}

        {visitedTabs.has("notifications") && (
          <div style={tabStyle("notifications")}>
            {isAddingNotifications ? (
              <NotificationSettings onClose={() => setIsAddingNotifications(false)} />
            ) : (
              <NotificationSettings onAddServiceClick={() => setIsAddingNotifications(true)} />
            )}
          </div>
        )}

        {visitedTabs.has("offersAndPromotions") && (
          <div style={tabStyle("offersAndPromotions")}>
            {isAddingOfferImage ? (
              <OfferImage onClose={() => setIsAddingOfferImage(false)} />
            ) : (
              <OfferImage onAddOfferImage={() => setIsAddingOfferImage(true)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const sidebarIcon = (key) => {
  switch (key) {
    case "dashboard":
      return "fa fa-dashboard";
    case "services":
      return "fa fa-cogs";
    case "reports":
      return "fa fa-chart-line";
    case "notifications":
      return "fa fa-bell";
    case "offersAndPromotions":
      return "fa fa-tags";
    default:
      return "fa fa-circle";
  }
};

export default ParentDashboard;
